/**
 * UnifiedSplashScreen Component
 * 
 * THE SINGLE SOURCE OF TRUTH for all loading/splash screens in the app.
 * 
 * Design spec (matching TotalAccess branding):
 * - Light or dark gradient background (follows `html.dark` / ThemeProvider)
 * - Centered TotalAccess logo with animated spinner ring
 * - "Loading your workspace..." message
 * - "One Platform. Complete Control." tagline
 * - "Powered by Total Access Platform" footer
 * 
 * Usage:
 * - Mount once at app root level
 * - Control via splashScreenService.show() and splashScreenService.hide()
 * - Can also be used directly as a component with visible prop
 * 
 * This replaces all other loading overlays to ensure consistent UX.
 * 
 * @author TotalAccess Development Team
 * @date February 2026
 */

'use client';

import React, { useState, useEffect, useCallback } from 'react';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';
import { splashScreenService, type SplashScreenState } from '@/lib/splashScreenService';
import { cn } from '@/lib/utils';

// Brand colors
const BRAND_PRIMARY = '#0f65fa';
const BRAND_TAGLINE = 'One Platform. Complete Control.';

interface UnifiedSplashScreenProps {
  /** Direct control - if provided, overrides service state */
  visible?: boolean;
  /** Custom message */
  message?: string;
  /** Custom sub-message */
  subMessage?: string;
  /** Show this as a global overlay (uses service state) */
  asGlobalOverlay?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Z-index level */
  zIndex?: number;
}

/**
 * The animated logo spinner component - can be reused elsewhere
 */
export function LogoSpinner({ 
  size = 'lg',
  primaryColor = BRAND_PRIMARY 
}: { 
  size?: 'sm' | 'md' | 'lg' | 'xl';
  primaryColor?: string;
}) {
  return (
    <BrandedLoadingSpinner
      mode="compact"
      size={size}
      primaryColor={primaryColor}
      showSurface={false}
      showProgressBar={false}
      className="leading-none"
    />
  );
}

/**
 * Full-screen unified splash screen component
 */
export default function UnifiedSplashScreen({
  visible: visibleProp,
  message: messageProp,
  subMessage: subMessageProp,
  asGlobalOverlay = false,
  className,
  zIndex = 99999,
}: UnifiedSplashScreenProps) {
  const [serviceState, setServiceState] = useState<SplashScreenState>(() => 
    splashScreenService.getState()
  );
  const [fadeOut, setFadeOut] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  
  // Subscribe to splash service state changes
  useEffect(() => {
    if (!asGlobalOverlay) return;
    
    const unsubscribe = splashScreenService.subscribe((state) => {
      setServiceState(state);
      if (state.isVisible) {
        setFadeOut(false);
        setShouldRender(true);
      } else {
        // Start fade out animation
        setFadeOut(true);
        // Remove from DOM after animation
        setTimeout(() => setShouldRender(false), 300);
      }
    });
    
    return unsubscribe;
  }, [asGlobalOverlay]);
  
  // Handle direct visibility prop
  useEffect(() => {
    if (asGlobalOverlay || visibleProp === undefined) return;
    
    if (visibleProp) {
      setFadeOut(false);
      setShouldRender(true);
    } else {
      setFadeOut(true);
      setTimeout(() => setShouldRender(false), 300);
    }
  }, [visibleProp, asGlobalOverlay]);
  
  // Determine visibility
  const isVisible = asGlobalOverlay ? serviceState.isVisible : visibleProp;
  const message = messageProp || serviceState.message || 'Loading your workspace...';
  const subMessage = subMessageProp || serviceState.subMessage;
  
  // Don't render if not visible and not in DOM
  if (!shouldRender && !isVisible) return null;
  
  return (
    <div 
      className={cn(
        'fixed inset-0 flex flex-col items-center justify-center transition-opacity duration-300',
        'bg-gradient-to-br from-gray-50 via-white to-gray-100',
        'dark:from-slate-950 dark:via-slate-900 dark:to-slate-950',
        fadeOut ? 'opacity-0 pointer-events-none' : 'opacity-100',
        className
      )}
      style={{ 
        zIndex,
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
      aria-busy="true"
      aria-label="Loading"
    >
      {/* Main content - centered */}
      <div className="flex flex-1 items-center justify-center px-6">
        <BrandedLoadingSpinner
          size="xl"
          message={message}
          subMessage={subMessage}
          primaryColor={BRAND_PRIMARY}
          showTagline={true}
          tagline={BRAND_TAGLINE}
          showProgressBar
          showSurface
          progress={typeof serviceState.progress === 'number' ? serviceState.progress : null}
          bootstrapShellCopy
          className="w-full max-w-xl"
        />
      </div>
      
      {/* Footer */}
      <div 
        className="absolute bottom-8 left-0 right-0 flex flex-col items-center gap-1"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <span className="text-xs text-gray-400 dark:text-slate-500">Powered by</span>
        <span className="text-sm font-semibold" style={{ color: BRAND_PRIMARY }}>
          Total Access Platform
        </span>
      </div>
    </div>
  );
}

/**
 * Hook to control splash screen from components
 */
export function useSplashScreen() {
  const show = useCallback((options?: { message?: string; subMessage?: string }) => {
    splashScreenService.show(options);
  }, []);
  
  const hide = useCallback((options?: { force?: boolean }) => {
    splashScreenService.hide(options);
  }, []);
  
  const update = useCallback((options: { message?: string; subMessage?: string; progress?: number }) => {
    splashScreenService.update(options);
  }, []);
  
  const withSplash = useCallback(<T,>(
    operation: () => Promise<T>,
    options?: { message?: string; subMessage?: string }
  ) => {
    return splashScreenService.withSplash(operation, options);
  }, []);
  
  return { show, hide, update, withSplash };
}

/**
 * Provider that renders the global splash overlay
 * Mount this once at app root level
 */
export function SplashScreenProvider({ children }: { children: React.ReactNode }) {
  return (
    <>
      {children}
      <UnifiedSplashScreen asGlobalOverlay />
    </>
  );
}
